<template>
    <v-container fluid>
        <v-row class="mt-n5 mb-2">
            <v-col cols="2">
                <h2 class="black--text">INDICATORS</h2>
            </v-col>
            <v-col cols="2">
                <span class="black--text mr-2">Initial month:</span>
                <v-select
                    v-model="startMonth"
                    :items="availableMonths"
                    item-text="label"
                    item-value="value"
                    dense
                    outlined
                    hide-details
                    :disabled="loading"
                    class="month-select mr-3"
                    style="min-width: 130px;"
                    @change="onStartMonthChange"
                />
            </v-col>
            <v-col cols="2">
                <span class="black--text mr-2">Final month:</span>
                <v-select
                    v-model="endMonth"
                    :items="allowedEndMonths"
                    item-text="label"
                    item-value="value"
                    dense
                    outlined
                    hide-details
                    :disabled="loading"
                    class="month-select mr-3"
                    style="min-width: 130px;"
                    @change="onRangeChange"
                />
            </v-col>
            <v-spacer />
            <v-col cols="2" class="d-flex justify-end">
                <v-btn
                    class="mr-2"
                    :loading="loading"
                    :disabled="loading"
                    color="primary"
                    @click="openIndicatorsReportDialog = true"
                >
                    Indicators Report
                </v-btn>
                <v-btn
                    color="primary"
                    :loading="loading"
                    :disabled="loading"
                    @click="openReport = true"
                >
                    Report
                </v-btn>
            </v-col>
        </v-row>
        <v-row v-if="rangeError" no-gutters class="mt-n2 mb-2">
            <v-col>
                <v-alert type="warning" dense text class="py-1">
                    {{ rangeError }}
                </v-alert>
            </v-col>
        </v-row>
        <v-row no-gutters>
            <v-col cols="12" xl="6" lg="6" md="6">
                <h2 class="mb-3 black--text">
                    Level of attention to purchases (Items)
                </h2>
                <v-data-table
                    :items="levelAttentionToPurchases"
                    :headers="headers"
                    class="elevation-0"
                    :mobile-breakpoint="0"
                    :loading="loading"
                    disable-pagination
                    hide-default-footer
                    :sort-by="['month']"
                    :sort-desc="[false]"
                >
                    <!--HEADERS-->
                    <template v-slot:[`header.new`]="{ header }">
                        <v-tooltip bottom>
                            <template v-slot:activator="{ on, attrs }">
                                <h3
                                    class="white--text"
                                    v-bind="attrs"
                                    v-on="on"
                                >
                                    {{ header.text }}
                                </h3>
                            </template>
                            <div>
                                <span>Pricing</span><br />
                                <span>preApproval</span><br />
                                <span>Approval</span><br />
                                <span>Purchasing</span><br />
                            </div>
                        </v-tooltip>
                    </template>
                    <template v-slot:[`header.accum`]="{ header }">
                        <v-tooltip bottom>
                            <template v-slot:activator="{ on, attrs }">
                                <h3
                                    class="white--text"
                                    v-bind="attrs"
                                    v-on="on"
                                >
                                    {{ header.text }}
                                </h3>
                            </template>
                            <div><span>Previous Month Pending</span><br /></div>
                        </v-tooltip>
                    </template>
                    <template v-slot:[`header.processed`]="{ header }">
                        <v-tooltip bottom>
                            <template v-slot:activator="{ on, attrs }">
                                <h3
                                    class="white--text"
                                    v-bind="attrs"
                                    v-on="on"
                                >
                                    {{ header.text }}
                                </h3>
                            </template>
                            <div>
                                <span>Intransit</span><br />
                                <span>Received</span><br />
                                <span>Ready to Close</span><br />
                                <span>Closed</span><br />
                            </div>
                        </v-tooltip>
                    </template>
                    <template v-slot:[`header.rejected`]="{ header }">
                        <v-tooltip bottom>
                            <template v-slot:activator="{ on, attrs }">
                                <h3
                                    class="white--text"
                                    v-bind="attrs"
                                    v-on="on"
                                >
                                    {{ header.text }}
                                </h3>
                            </template>
                            <div><span>Rejected This Month</span><br /></div>
                        </v-tooltip>
                    </template>
                    <template v-slot:[`header.pending`]="{ header }">
                        <v-tooltip bottom>
                            <template v-slot:activator="{ on, attrs }">
                                <h3
                                    class="white--text"
                                    v-bind="attrs"
                                    v-on="on"
                                >
                                    {{ header.text }}
                                </h3>
                            </template>
                            <div>
                                <span>Pricing</span><br />
                                <span>PreApproval</span><br />
                                <span>Approval</span><br />
                                <span>Purchasing</span><br />
                            </div>
                        </v-tooltip>
                    </template>
                    <template v-slot:[`header.performance`]="{ header }">
                        <v-tooltip bottom>
                            <template v-slot:activator="{ on, attrs }">
                                <h3
                                    class="white--text"
                                    v-bind="attrs"
                                    v-on="on"
                                >
                                    {{ header.text }}
                                </h3>
                            </template>
                            <div>
                                <span
                                    >Processed / (New + Accum - Rejected)</span
                                >
                            </div>
                        </v-tooltip>
                    </template>
                    <!--ITEMS-->
                    <template v-slot:[`item.month`]="{ item }">
                        <p class="my-0 text-capitalize">
                            {{ formatMonth(item.month) }}
                        </p>
                    </template>
                    <template v-slot:[`item.performance`]="{ item }">
                        <td
                            :style="
                                `background-color: ${getColorPerformance(
                                    item.performance
                                )} !important`
                            "
                        >
                            <p class="white--text text-center my-0">
                                {{ item.performance.toFixed(2) }}
                            </p>
                        </td>
                    </template>
                </v-data-table>
            </v-col>
            <v-col cols="12" xl="6" lg="6" md="6">
                <Highcharts :options="levelAttentionChart" />
            </v-col>
        </v-row>
        <v-row no-gutters class="my-2">
            <v-col cols="12" xl="6" lg="6" md="6">
                <h2 class="mb-3 black--text">
                    Order Lifecycle Overview
                </h2>
                <v-data-table
                    :items="allOrders"
                    :headers="headersAllOrders"
                    class="elevation-0"
                    :mobile-breakpoint="0"
                    :loading="loading"
                    disable-pagination
                    hide-default-footer
                    :sort-by="['month']"
                    :sort-desc="[false]"
                >
                    <template v-slot:[`item.month`]="{ item }">
                        <p class="my-0 text-capitalize">
                            {{ formatMonth(item.month) }}
                        </p>
                    </template>
                    <template v-slot:[`item.performance`]="{ item }">
                        <td
                            :style="
                                `background-color: ${getColorPerformance(
                                    item.performance
                                )} !important`
                            "
                        >
                            <p class="white--text text-center my-0">
                                {{ item.performance.toFixed(2) }}
                            </p>
                        </td>
                    </template>
                </v-data-table>
            </v-col>
            <v-col cols="12" xl="6" lg="6" md="6">
                <Highcharts :options="allOrdersChart" />
            </v-col>
        </v-row>

        <v-dialog
            :retain-focus="false"
            persistent
            width="400"
            v-model="openReport"
        >
            <Report v-if="openReport" @close="closeReport" />
        </v-dialog>

        <v-dialog
            v-model="openIndicatorsReportDialog"
            max-width="400"
            persistent
        >
            <v-card>
                <v-card-title class="headline">
                    <v-btn
                        small
                        icon
                        @click="openIndicatorsReportDialog = false"
                        class="mr-2"
                        style="background-color: transparent !important;"
                    >
                        <v-icon color="primary">
                            mdi-close
                        </v-icon>
                    </v-btn>
                    Select month for report
                </v-card-title>
                <v-divider class="ml-4 mr-5" />
                <v-card-text>
                    <v-row no-gutters class="mt-4 mb-n2">
                        <v-col cols="12">
                            <v-select
                                v-model="selectedReportMonth"
                                :items="reportMonthOptions"
                                item-text="label"
                                item-value="value"
                                label="Month"
                                dense
                                outlined
                                hide-details
                                :menu-props="reportMonthMenuProps"
                                @click.native="attachReportMonthMenuScroll"
                            />
                            <p
                                v-if="reportFiscalRangeHint"
                                class="caption grey--text text--darken-1 mt-2 mb-0"
                            >
                                Period: {{ reportFiscalRangeHint }}
                            </p>
                        </v-col>
                    </v-row>
                </v-card-text>
                <v-card-actions>
                    <v-spacer />
                    <v-btn
                        color="primary"
                        class="mb-3 mr-2 mt-n2"
                        @click="confirmIndicatorsReport"
                    >
                        Continue
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </v-container>
</template>

<script>
import { mapMutations } from 'vuex'
import API from '@/services/api'
import { Chart } from 'highcharts-vue'
import Report from '@/components/Purchasing/Report.vue'
import moment from 'moment'
import momentTimeZone from 'moment-timezone'
import * as XLSX from 'xlsx/xlsx.mjs'

export default {
    name: 'Indicators',
    components: {
        Highcharts: Chart,
        Report,
    },
    data: () => ({
        openReport: false,
        openIndicatorsReportDialog: false,
        loading: false,
        startMonth: null,
        endMonth: null,
        rangeError: null,
        monthDates: [],
        levelAttentionToPurchases: [],
        levelAttentionChart: {},
        allOrdersChart: {},
        allOrders: [],
        users: [],
        headers: [
            {
                text: 'Month',
                value: 'month',
                sortable: false,
                align: 'center',
            },
            {
                text: 'New',
                value: 'new',
                sortable: false,
                align: 'center',
            },
            {
                text: 'Accum',
                value: 'accum',
                sortable: false,
                align: 'center',
            },
            {
                text: 'Processed',
                value: 'processed',
                sortable: false,
                align: 'center',
            },
            {
                text: 'Rejected',
                value: 'rejected',
                sortable: false,
                align: 'center',
            },
            {
                text: 'Pending',
                value: 'pending',
                sortable: false,
                align: 'center',
            },
            {
                text: '%',
                value: 'performance',
                sortable: false,
                align: 'center',
            },
        ],
        headersAllOrders: [
            { text: 'Month', value: 'month', sortable: false, align: 'center' },
            {
                text: 'Received',
                value: 'received',
                sortable: false,
                align: 'center',
            },

            {
                text: 'On Time',
                value: 'onTime',
                sortable: false,
                align: 'center',
            },
            {
                text: 'Delayed',
                value: 'delayed',
                sortable: false,
                align: 'center',
            },
            {
                text: '% On Time',
                value: 'performance',
                sortable: false,
                align: 'center',
            },
        ],

        monthsToShow: [],
        inform: {},
        selectedReportMonth: moment().format('YYYY-MM'),
        reportMonthMenuProps: {
            offsetY: true,
            maxHeight: 280,
            contentClass: 'indicators-report-month-menu',
        },
    }),
    computed: {
        availableMonths() {
            const list = []
            const end = moment().startOf('month')
            for (let i = 12; i >= 0; i--) {
                const m = end.clone().subtract(i, 'months')
                list.push({
                    value: m.format('YYYY-MM'),
                    label: m.format('MMM YYYY'),
                })
            }
            return list
        },
        reportMonthOptions() {
            const list = []
            const end = moment().startOf('month')
            for (let i = 12; i >= 0; i--) {
                const m = end.clone().subtract(i, 'months')
                list.push({
                    value: m.format('YYYY-MM'),
                    label: m.format('MMM YYYY'),
                })
            }
            return list
        },
        reportFiscalRangeHint() {
            if (!this.selectedReportMonth) return ''
            const range = this.periodForMonth(this.selectedReportMonth)
            if (!range) return ''
            const start = moment(range.start, 'YYYY-MM-DD')
            const end = moment(range.end, 'YYYY-MM-DD')
            return `${start.format('MMM D, YYYY')} – ${end.format(
                'MMM D, YYYY'
            )}`
        },
        allowedEndMonths() {
            if (!this.startMonth) return []
            const start = momentTimeZone(this.startMonth, 'YYYY-MM').utcOffset(
                -5
            )
            const maxAvailable = momentTimeZone(
                this.openMonthLabel(),
                'YYYY-MM'
            ).utcOffset(-5)
            const minEnd = start.clone().add(0, 'months')
            const maxEnd = start.clone().add(5, 'months')
            const endCap = maxEnd.isAfter(maxAvailable) ? maxAvailable : maxEnd
            if (minEnd.isAfter(endCap)) return []
            const list = []
            const current = minEnd.clone()
            while (!current.isAfter(endCap, 'month')) {
                list.push({
                    value: current.format('YYYY-MM'),
                    label: current.format('MMM YYYY'),
                })
                current.add(1, 'month')
            }
            return list
        },
    },
    created() {
        const openMonth = momentTimeZone(
            this.openMonthLabel(),
            'YYYY-MM'
        ).utcOffset(-5)
        this.endMonth = openMonth.format('YYYY-MM')
        this.startMonth = openMonth
            .clone()
            .subtract(5, 'months')
            .format('YYYY-MM')
        this.levelAttentionChart = {
            title: {
                text: '',
            },
            xAxis: {
                type: 'category',
                title: {
                    text: '',
                },
            },
            yAxis: {
                title: {
                    text: '',
                },
            },
            legend: {
                enabled: false,
            },
            series: [],
            credits: {
                enabled: false,
            },
            accessibility: { enabled: false },
        }
        this.allOrdersChart = {
            title: { text: '' },
            xAxis: { type: 'category', title: { text: '' } },
            yAxis: { title: { text: '' } },
            legend: { enabled: false },
            series: [],
            credits: { enabled: false },
            accessibility: { enabled: false },
        }
    },
    async mounted() {
        await this.fetchAndRenderIndicators()
    },
    methods: {
        ...mapMutations(['setErrorItems']),
        periodForMonth(monthLabel) {
            const m = moment(monthLabel, 'YYYY-MM', true)
            if (!m.isValid()) return null
            if (monthLabel < '2026-09') {
                return {
                    start: m
                        .clone()
                        .subtract(1, 'month')
                        .date(25)
                        .format('YYYY-MM-DD'),
                    end: m
                        .clone()
                        .date(24)
                        .format('YYYY-MM-DD'),
                }
            }
            if (monthLabel === '2026-09') {
                return { start: '2026-08-25', end: '2026-09-30' }
            }
            return {
                start: m
                    .clone()
                    .startOf('month')
                    .format('YYYY-MM-DD'),
                end: m
                    .clone()
                    .endOf('month')
                    .format('YYYY-MM-DD'),
            }
        },
        openMonthLabel(now = momentTimeZone().utcOffset(-5)) {
            const clock = momentTimeZone(now).utcOffset(-5)
            const ymd = clock.format('YYYY-MM-DD')
            if (ymd <= '2026-08-24') {
                const open =
                    clock.date() > 24
                        ? clock.clone().add(1, 'month')
                        : clock.clone()
                return open.format('YYYY-MM')
            }
            if (ymd <= '2026-09-30') {
                return '2026-09'
            }
            return clock.format('YYYY-MM')
        },
        async fetchAndRenderIndicators() {
            if (!this.validateRange()) return
            try {
                this.loading = true
                this.getDates()
                const start = this.monthDates[0].start
                const end = this.monthDates[this.monthDates.length - 1].end
                const [inform, usersResponse] = await Promise.all([
                    API.purchaseIndicators({
                        start,
                        end,
                    }),
                    this.users.length
                        ? Promise.resolve(null)
                        : API.getLiteUsers(),
                ])
                this.inform = inform
                if (usersResponse) {
                    this.users = usersResponse.data.users || []
                }
                this.structureData(this.inform)
                this.printLevelAttentionChart()
                this.printAllOrdersChart()
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
            } finally {
                this.loading = false
            }
        },
        async generateXLS() {
            try {
                this.loading = true
                const month = this.selectedReportMonth
                const report = await API.getReportIndicators({ month })
                const detailRows = Array.isArray(report?.rows)
                    ? report.rows
                    : []

                const wb = XLSX.utils.book_new()
                const wsDetail = XLSX.utils.json_to_sheet(detailRows)
                XLSX.utils.book_append_sheet(wb, wsDetail, 'Indicators')
                const rangeLabel = report?.range
                    ? `${report.range.start}_to_${report.range.end}`
                    : month
                XLSX.writeFile(wb, `indicatorsReport_${rangeLabel}.xlsx`)
            } catch (error) {
                if (error.name === 'AbortError') {
                    return
                }
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
            } finally {
                this.loading = false
            }
        },
        attachReportMonthMenuScroll(attempt = 0) {
            const menu = document.querySelector('.indicators-report-month-menu')
            if (!menu) {
                if (attempt < 5) {
                    requestAnimationFrame(() =>
                        this.attachReportMonthMenuScroll(attempt + 1)
                    )
                }
                return
            }
            if (menu.dataset.reportMonthWheel === '1') return
            menu.dataset.reportMonthWheel = '1'
            menu.addEventListener('wheel', event => {
                if (menu.scrollHeight <= menu.clientHeight) return
                event.stopPropagation()
            })
        },
        confirmIndicatorsReport() {
            if (!this.selectedReportMonth) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: 'Please select a month.',
                })
                return
            }

            this.openIndicatorsReportDialog = false
            this.generateXLS()
        },
        getUser(id) {
            const user = this.users.find(u => u.id == id)
            return user?.name || null
        },
        formatDate(seconds) {
            return momentTimeZone
                .unix(seconds)
                .utcOffset(-5)
                .format('YYYY-MM-DD HH:mm:ss')
        },
        formatDate2(seconds) {
            return momentTimeZone
                .unix(seconds)
                .utcOffset(-5)
                .format('YYYY-MM-DD')
        },
        structureData(inform) {
            this.allOrders = []
            this.levelAttentionToPurchases = []

            Object.keys(inform).forEach(key => {
                const periodEnd = key.split('_to_')[1]
                const periodMonth = periodEnd.slice(0, 7)
                const levelOfAttentionRow = {
                    month: periodMonth,
                    new: inform[key].new,
                    accum: inform[key].accum,
                    processed: inform[key].processed,
                    rejected: inform[key].rejected,
                    pending: inform[key].pending,
                }

                const allRow = {
                    month: periodMonth,
                    received: inform[key].toReceive || 0,
                    onTime: inform[key].onTime || 0,
                    delayed: inform[key].delayed || 0,
                }
                const total = allRow.onTime + allRow.delayed
                allRow.performance =
                    total > 0 ? (allRow.onTime * 100) / total : 0
                this.allOrders.push(allRow)

                this.levelAttentionToPurchases.push(levelOfAttentionRow)
            })

            for (const index in this.levelAttentionToPurchases) {
                const row = this.levelAttentionToPurchases[index]
                this.levelAttentionToPurchases[index].performance =
                    (row.processed * 100) / (row.new + row.accum)
            }

            this.levelAttentionToPurchases = this.levelAttentionToPurchases.filter(
                row => this.monthsToShow.includes(row.month)
            )

            this.levelAttentionToPurchases.sort(
                (a, b) => moment(a.month) - moment(b.month)
            )

            this.allOrders.sort((a, b) => moment(a.month) - moment(b.month))
            this.allOrders = this.allOrders.filter(row =>
                this.monthsToShow.includes(row.month)
            )
        },
        formatMonth(monthStr) {
            const period = this.monthDates.find(d => d.end.startsWith(monthStr))
            if (period) {
                return moment(period.end).format('MMM')
            }
            return moment(monthStr, 'YYYY-MM').format('MMM')
        },

        closeReport() {
            this.openReport = false
        },

        printAllOrdersChart() {
            this.allOrdersChart = {
                xAxis: {
                    categories: this.allOrders.map(item => item.month),
                    title: { text: '' },
                    type: 'category',
                },
                yAxis: {
                    title: { text: '' },
                },
                legend: { enabled: false },
                credits: { enabled: false },
                accessibility: { enabled: false },
                series: [
                    {
                        name: 'Performance',
                        data: this.allOrders.map(item => ({
                            name: item.month,
                            y: parseFloat(item.performance.toFixed(2)),
                        })),
                        dataLabels: {
                            enabled: true,
                            format: '{y}',
                        },
                    },
                ],
            }
        },

        getColorPerformance(performance) {
            let convertNumber = Number(performance)

            if (convertNumber >= 85) {
                return '#9acd32'
            } else if (convertNumber >= 60 && convertNumber < 85) {
                return '#FFC107'
            } else if (convertNumber < 60) {
                return '#ff0000'
            }
            return '#ccc'
        },

        printLevelAttentionChart() {
            this.levelAttentionChart.xAxis.categories = this.levelAttentionToPurchases.map(
                item => item.month
            )
            this.levelAttentionChart.series = [
                {
                    name: 'performance',
                    data: this.levelAttentionToPurchases.map(item => ({
                        name: item.month,
                        y: parseFloat(item.performance.toFixed(2)),
                    })),
                    dataLabels: {
                        enabled: true,
                        format: '{y}',
                    },
                },
            ]
        },
        getDates() {
            this.monthDates = []
            this.monthsToShow = []
            if (!this.startMonth || !this.endMonth) return

            const start = moment(this.startMonth, 'YYYY-MM')
            const end = moment(this.endMonth, 'YYYY-MM')
            if (start.isAfter(end)) return

            const monthsDiff = end.diff(start, 'months') + 1
            if (monthsDiff < 1 || monthsDiff > 6) return

            const current = start.clone()
            while (current.isSameOrBefore(end, 'month')) {
                const monthStr = current.format('YYYY-MM')
                this.monthDates.push({ start: monthStr, end: monthStr })
                this.monthsToShow.push(monthStr)
                current.add(1, 'month')
            }
        },

        validateRange() {
            if (!this.startMonth || !this.endMonth) {
                this.rangeError = 'Select start month and end month.'
                return false
            }
            const start = moment(this.startMonth, 'YYYY-MM')
            const end = moment(this.endMonth, 'YYYY-MM')
            if (start.isAfter(end)) {
                this.rangeError =
                    'The initial month must be earlier than or equal to the final month.'
                return false
            }
            const monthsDiff = end.diff(start, 'months') + 1
            if (monthsDiff < 0) {
                this.rangeError = 'The interval should be at least 1 months.'
                return false
            }
            if (monthsDiff > 6) {
                this.rangeError =
                    'The interval should be a maximum of 6 months.'
                return false
            }
            this.rangeError = null
            return true
        },

        async loadIndicators() {
            await this.fetchAndRenderIndicators()
        },

        onStartMonthChange() {
            if (!this.startMonth) return
            const start = moment(this.startMonth, 'YYYY-MM')
            const end = this.endMonth ? moment(this.endMonth, 'YYYY-MM') : null
            const maxMonth = moment(this.openMonthLabel(), 'YYYY-MM')
            const defaultEnd = start.clone().add(5, 'months')
            const cappedEnd = defaultEnd.isAfter(maxMonth)
                ? maxMonth
                : defaultEnd
            const newEndStr = cappedEnd.format('YYYY-MM')
            const rangeMonths = end ? end.diff(start, 'months') + 1 : 0
            if (!end || start.isAfter(end) || rangeMonths > 6) {
                this.endMonth = newEndStr
            }
            const allowed = this.allowedEndMonths
            if (
                allowed.length &&
                !allowed.some(o => o.value === this.endMonth)
            ) {
                this.endMonth = allowed[0].value
            } else if (allowed.length === 0) {
                this.endMonth = null
            }
            this.$nextTick(() => this.loadIndicators())
        },

        onRangeChange() {
            this.loadIndicators()
        },
    },
}
</script>

<style scoped>
::v-deep(.v-data-table-header th) {
    background-color: #1976d2 !important;
}
::v-deep(span) {
    color: #ffffff;
}
.v-data-table {
    border: 1px solid #eeeeee;
}
</style>

<style>
.v-menu__content.indicators-report-month-menu {
    overflow-y: auto !important;
}
</style>
